'use client'

import { useState, useMemo } from 'react'
import { useDashboardData } from '@/hooks/useDashboardData'
import { useFilteredData } from '@/hooks/useFilters'
import { computeSlaStatus, computeLongRunningTasks, isRecurring } from '@/lib/calculations'
import DataTable from '@/components/ui/DataTable'
import Badge, { CatBadge } from '@/components/ui/Badge'
import type { Task } from '@/types'

const DAYS_90 = 90 * 86400000

function TaskLink({ task, odooUrl }: { task: Task; odooUrl: string }) {
  return (
    <a href={`${odooUrl}/odoo/project.project/${task.projId}/tasks/${task.id}`} target="_blank" rel="noopener noreferrer"
      className="text-slate-900 dark:text-slate-100 hover:underline font-medium text-sm truncate block max-w-[260px]" title={task.name}>
      {task.name}
    </a>
  )
}

function ProjLink({ task, odooUrl }: { task: Task; odooUrl: string }) {
  return (
    <a href={`${odooUrl}/odoo/project.project/${task.projId}/tasks`} target="_blank" rel="noopener noreferrer"
      className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 text-xs truncate block max-w-[180px]">
      {task.proj}
    </a>
  )
}

export default function DeliveryPage() {
  const { isLoading, odooUrl, activeEngineers, activeProjectIds } = useDashboardData()
  const { filteredTasks, activeTasks, inProg } = useFilteredData()
  const [slaTab, setSlaTab] = useState<'team' | 'leftover'>('team')

  // Recurring tasks are excluded everywhere on this page — not just their own section.
  const regularActiveTasks = useMemo(() => activeTasks.filter(t => !isRecurring(t)), [activeTasks])
  const regularFilteredTasks = useMemo(() => filteredTasks.filter(t => !isRecurring(t)), [filteredTasks])
  const regularInProg = useMemo(() => inProg.filter(t => !isRecurring(t)), [inProg])

  const slaData = useMemo(() => {
    const process = (tasks: Task[]) => tasks.filter(t => t.age !== null).map(t => {
      const { sla, slaPct } = computeSlaStatus(t)
      return { ...t, sla, slaPct }
    }).sort((a, b) => b.slaPct - a.slaPct)

    const team = process(regularActiveTasks)
    const leftover = process(regularFilteredTasks.filter(t => {
      if (t.cancelled || t.stage === 'Done' || t.stage === 'Backlog') return false
      if (!activeProjectIds.includes(t.projId)) return false
      return t.users.length === 0 || !t.users.some(u => activeEngineers.includes(u))
    }))
    return { team, leftover, teamBreached: team.filter(t => t.slaPct >= 100).length, teamWarning: team.filter(t => t.slaPct >= 80 && t.slaPct < 100).length }
  }, [regularActiveTasks, regularFilteredTasks, activeProjectIds, activeEngineers])

  const longRunningTasks = useMemo(() => computeLongRunningTasks(regularActiveTasks, 90), [regularActiveTasks])

  // Estimation vs Actual: completed, estimated tasks from the last 3 months, per engineer.
  const recentEstTasks = useMemo(() => {
    const cutoff = Date.now() - DAYS_90
    return regularFilteredTasks.filter(t =>
      t.stage === 'Done' && t.alloc_h > 0 && t.eff_h > 0 && t.last_stage && new Date(t.last_stage).getTime() >= cutoff,
    )
  }, [regularFilteredTasks])

  const estByEngineer = useMemo(() => {
    const map: Record<string, Task[]> = {}
    recentEstTasks.forEach(t => new Set(t.users).forEach(u => {
      if (!map[u]) map[u] = []
      map[u].push(t)
    }))
    return Object.entries(map).sort(([, a], [, b]) => b.length - a.length)
  }, [recentEstTasks])

  if (isLoading) return <div className="text-slate-400 text-center py-16">Loading...</div>

  const today = new Date().toISOString().slice(0, 10)

  const slaColumns = [
    { key: 'name', label: 'Task', render: (t: Task & { sla: number; slaPct: number }) => <TaskLink task={t} odooUrl={odooUrl} />, sortValue: (t: Task) => t.name },
    { key: 'proj', label: 'Project', render: (t: Task & { sla: number; slaPct: number }) => <ProjLink task={t} odooUrl={odooUrl} />, sortValue: (t: Task) => t.proj },
    { key: 'stage', label: 'Stage', render: (t: Task) => <Badge>{t.stage}</Badge>, sortValue: (t: Task) => t.stage },
    { key: 'age', label: 'Age', render: (t: Task) => <span className="text-xs">{t.age}d</span>, sortValue: (t: Task) => t.age || 0 },
    { key: 'sla', label: 'SLA', render: (t: Task & { sla: number }) => <span className="text-xs text-slate-400">{t.sla}d</span>, sortValue: (t: Task & { sla: number }) => t.sla },
    { key: 'status', label: 'Status', render: (t: Task & { slaPct: number }) => <Badge variant={t.slaPct >= 100 ? 'danger' : t.slaPct >= 80 ? 'warning' : 'default'}>{t.slaPct >= 100 ? 'BREACH' : t.slaPct >= 80 ? 'WARNING' : `${t.slaPct.toFixed(0)}%`}</Badge>, sortValue: (t: Task & { slaPct: number }) => t.slaPct },
    {
      key: 'deadline', label: 'Deadline',
      render: (t: Task) => t.deadline
        ? <span className={`text-xs ${t.deadline < today ? 'text-red-600 dark:text-red-400 font-semibold' : 'text-slate-400'}`}>{t.deadline}{t.deadline < today ? ' (overdue)' : ''}</span>
        : <span className="text-xs text-slate-400">—</span>,
      sortValue: (t: Task) => t.deadline || '',
    },
    { key: 'assigned', label: 'Assigned', render: (t: Task) => <span className="text-xs text-slate-400">{t.users.join(', ') || '—'}</span>, sortValue: (t: Task) => t.users.join(', ') },
  ]

  return (
    <div className="space-y-5">
      {/* SLA */}
      <section className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-5">
        <h3 className="text-base font-semibold mb-1">SLA <span className="text-xs font-normal text-slate-400">{slaData.teamBreached} breached, {slaData.teamWarning} warning</span></h3>
        <div className="flex gap-1 mb-4 mt-2">
          {(['team', 'leftover'] as const).map(tab => (
            <button key={tab} onClick={() => setSlaTab(tab)} className={`px-3 py-1 text-sm ${slaTab === tab ? 'bg-blue-600 text-white' : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'}`}>
              {tab === 'team' ? 'Core Team' : `Leftover (${slaData.leftover.length})`}
            </button>
          ))}
        </div>
        <DataTable data={slaTab === 'team' ? slaData.team.filter(t => t.slaPct >= 50) : slaData.leftover} columns={slaColumns as Parameters<typeof DataTable>[0]['columns']} />
      </section>

      {/* Tasks Over 90 Days */}
      <section className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-5">
        <h3 className="text-base font-semibold mb-1">Tasks Over 90 Days <span className="text-xs font-normal text-slate-400">{longRunningTasks.length}</span></h3>
        <p className="text-xs text-slate-400 mb-4">Active tasks open more than 90 business days, regardless of stage.</p>
        {longRunningTasks.length === 0 ? (
          <div className="text-sm text-slate-400 py-4 text-center">Nothing open that long right now.</div>
        ) : (
          <DataTable data={longRunningTasks} columns={[
            { key: 'name', label: 'Task', render: t => <TaskLink task={t} odooUrl={odooUrl} />, sortValue: t => t.name },
            { key: 'proj', label: 'Project', render: t => <ProjLink task={t} odooUrl={odooUrl} />, sortValue: t => t.proj },
            { key: 'cat', label: 'Cat', render: t => <CatBadge cat={t.cat} />, sortValue: t => t.cat },
            { key: 'stage', label: 'Stage', render: t => <Badge>{t.stage}</Badge>, sortValue: t => t.stage },
            { key: 'assigned', label: 'Assigned', render: t => <span className="text-xs text-slate-400">{t.users.join(', ') || '—'}</span>, sortValue: t => t.users.join(', ') },
            { key: 'age', label: 'Age', render: t => <span className="text-xs font-semibold">{t.age}d</span>, sortValue: t => t.age || 0 },
          ]} />
        )}
      </section>

      {/* Estimation vs Actual */}
      <section className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-5">
        <h3 className="text-base font-semibold mb-1">Estimation vs Actual <span className="text-xs font-normal text-slate-400">{recentEstTasks.length} tasks · last 3 months</span></h3>
        <p className="text-xs text-slate-400 mb-4">Completed, estimated tasks per engineer. 100% = perfect estimate.</p>
        {estByEngineer.length === 0 ? (
          <div className="text-sm text-slate-400 py-4 text-center">No completed, estimated tasks in the last 3 months.</div>
        ) : (
          <div className="space-y-5">
            {estByEngineer.map(([name, tasks]) => (
              <div key={name}>
                <div className="text-sm font-semibold mb-1.5">{name} <span className="text-xs font-normal text-slate-400">({tasks.length})</span></div>
                <DataTable data={tasks} columns={[
                  { key: 'name', label: 'Task', render: t => <TaskLink task={t} odooUrl={odooUrl} />, sortValue: t => t.name },
                  { key: 'proj', label: 'Project', render: t => <ProjLink task={t} odooUrl={odooUrl} />, sortValue: t => t.proj },
                  { key: 'cat', label: 'Cat', render: t => <CatBadge cat={t.cat} />, sortValue: t => t.cat },
                  { key: 'est', label: 'Est', render: t => <span className="text-xs text-slate-400">{t.alloc_h.toFixed(0)}h</span>, sortValue: t => t.alloc_h },
                  { key: 'actual', label: 'Actual', render: t => <span className="text-xs text-slate-400">{t.eff_h.toFixed(0)}h</span>, sortValue: t => t.eff_h },
                  {
                    key: 'acc', label: 'Acc',
                    render: t => { const acc = (t.eff_h / t.alloc_h) * 100; return <Badge variant={acc > 130 ? 'danger' : acc > 110 ? 'warning' : 'success'}>{acc.toFixed(0)}%</Badge> },
                    sortValue: t => (t.eff_h / t.alloc_h) * 100,
                  },
                ]} />
              </div>
            ))}
          </div>
        )}
      </section>

      {/* In Progress */}
      <section className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-5">
        <h3 className="text-base font-semibold mb-1">In Progress <span className="text-xs font-normal text-slate-400">{regularInProg.length}</span></h3>
        <DataTable data={regularInProg.sort((a, b) => (b.age || 0) - (a.age || 0))} columns={[
          { key: 'name', label: 'Task', render: t => <TaskLink task={t} odooUrl={odooUrl} />, sortValue: t => t.name },
          { key: 'proj', label: 'Project', render: t => <ProjLink task={t} odooUrl={odooUrl} />, sortValue: t => t.proj },
          { key: 'cat', label: 'Cat', render: t => <CatBadge cat={t.cat} />, sortValue: t => t.cat },
          { key: 'assigned', label: 'Assigned', render: t => <span className="text-xs">{t.users.join(', ') || '—'}</span>, sortValue: t => t.users.join(', ') },
          { key: 'age', label: 'Age', render: t => <span className="text-xs">{t.age !== null ? `${t.age}d` : '—'}</span>, sortValue: t => t.age || 0 },
          { key: 'hours', label: 'Hours', render: t => <span className="text-xs text-slate-400">{t.eff_h ? `${t.eff_h.toFixed(1)}h` : '—'}{t.alloc_h ? `/${t.alloc_h}h` : ''}</span>, sortValue: t => t.eff_h },
        ]} />
      </section>
    </div>
  )
}
